import React, { useState, useEffect } from 'react';
import { Flame, Eye, EyeOff, AlertTriangle, Info, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Scope1 = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    emissions: [],
    showBreakdown: false,
    showSourceRow: false,
    assumptions: [],
    ...initialData
  });

  // Load initial data when component mounts or initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  // Auto-save to localStorage whenever formData changes
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      try {
        const currentData = JSON.parse(localStorage.getItem('calculationWorkflowData') || '{}');
        const updatedData = {
          ...currentData,
          scope1: formData
        };
        localStorage.setItem('calculationWorkflowData', JSON.stringify(updatedData));
      } catch (error) {
        console.error('Error saving Scope1 data to localStorage:', error);
      }
    }
  }, [formData]);

  const [errors] = useState({});
  const [warnings] = useState({});

  // Sample data - in real app this would come from data_freeze_v1
  const sampleEmissions = React.useMemo(() => [
    {
      id: 1,
      site: 'Headquarters',
      fuelType: 'Natural Gas',
      activity: 15000,
      unit: 'kWh',
      emissionFactor: 0.202,
      unitEF: 'kg CO2e/kWh',
      co2: 2800,
      ch4: 0.5,
      n2o: 0.1,
      totalCO2e: 3030,
      source: 'Data Log Entry #1234',
      previousPeriod: 2900,
      outlier: false,
      density: null
    },
    {
      id: 2,
      site: 'Manufacturing Plant',
      fuelType: 'Diesel',
      activity: 5000,
      unit: 'L',
      emissionFactor: 2.68,
      unitEF: 'kg CO2e/L',
      co2: 12500,
      ch4: 2.1,
      n2o: 0.8,
      totalCO2e: 13400,
      source: 'Data Log Entry #1235',
      previousPeriod: 12000,
      outlier: true,
      density: 0.832
    },
    {
      id: 3,
      site: 'Warehouse',
      fuelType: 'Propane',
      activity: 2000,
      unit: 'kg',
      emissionFactor: 3.01,
      unitEF: 'kg CO2e/kg',
      co2: 5800,
      ch4: 0.3,
      n2o: 0.05,
      totalCO2e: 6020,
      source: 'Data Log Entry #1236',
      previousPeriod: 6100,
      outlier: false,
      density: null
    },
    {
      id: 4,
      site: 'Office Building',
      fuelType: 'Refrigerant R-134a',
      activity: 50,
      unit: 'kg',
      emissionFactor: 1430,
      unitEF: 'kg CO2e/kg',
      co2: 0,
      ch4: 0,
      n2o: 0,
      totalCO2e: 71500,
      source: 'Data Log Entry #1237',
      previousPeriod: 72000,
      outlier: false,
      density: null
    }
  ], []);

  // Initialize with sample data if empty
  useEffect(() => {
    if (!formData.emissions || formData.emissions.length === 0) {
      setFormData(prev => ({
        ...prev,
        emissions: sampleEmissions
      }));
    }
  }, [formData.emissions, sampleEmissions]);



  const toggleBreakdown = () => {
    setFormData(prev => ({
      ...prev,
      showBreakdown: !prev.showBreakdown
    }));
  };

  const toggleSourceRow = () => {
    setFormData(prev => ({
      ...prev,
      showSourceRow: !prev.showSourceRow
    }));
  };





  const totalEmissions = formData.emissions.reduce((sum, emission) => sum + emission.totalCO2e, 0);

  const getOutlierClass = (emission) => {
    if (emission.outlier) {
      return 'bg-yellow-50 border-yellow-200';
    }
    return 'bg-white border-gray-200';
  };

  const getErrorClass = (emissionId) => {
    return errors[emissionId] ? 'border-red-500 bg-red-50' : '';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
          <Flame className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t('workflow.calculation.scope1.title')}</h2>
          <p className="text-gray-600">{t('workflow.calculation.scope1.description')}</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Summary Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-blue-900">{t('workflow.calculation.scope1.summary.title')}</h3>
              <p className="text-blue-700">{t('workflow.calculation.scope1.summary.total', { total: (totalEmissions / 1000).toFixed(2) })}</p>
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={toggleBreakdown}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  formData.showBreakdown
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-blue-600 border border-blue-300 hover:bg-blue-50'
                }`}
              >
                {formData.showBreakdown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span>{formData.showBreakdown ? t('workflow.calculation.scope1.controls.hideBreakdown') : t('workflow.calculation.scope1.controls.showBreakdown')}</span>
              </button>
              <button
                type="button"
                onClick={toggleSourceRow}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  formData.showSourceRow
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-green-600 border border-green-300 hover:bg-green-50'
                }`}
              >
                <ExternalLink className="w-4 h-4" />
                <span>{formData.showSourceRow ? t('workflow.calculation.scope1.controls.hideSource') : t('workflow.calculation.scope1.controls.showSource')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Emissions Table */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.calculation.scope1.table.title')}</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope1.table.site')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope1.table.fuelType')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope1.table.activity')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope1.table.ef')}</th>
                  {formData.showBreakdown && (
                    <>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope1.table.co2')}</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope1.table.ch4')}</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope1.table.n2o')}</th>
                    </>
                  )}
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope1.table.total')}</th>
                  {formData.showSourceRow && (
                    <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope1.table.source')}</th>
                  )}
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope1.table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {formData.emissions.map((emission) => (
                  <tr key={emission.id} className={`border-b ${getOutlierClass(emission)} ${getErrorClass(emission.id)}`}>
                    <td className="py-3 px-4 font-medium text-gray-900">{emission.site}</td>
                    <td className="py-3 px-4 text-gray-700">{emission.fuelType}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-1">
                        <span className="text-gray-700">{emission.activity.toLocaleString()}</span>
                        <span className="text-gray-500 text-sm">{emission.unit}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-1">
                        <span className="text-gray-700">{emission.emissionFactor}</span>
                        <span className="text-gray-500 text-sm">{emission.unitEF}</span>
                      </div>
                    </td>
                    {formData.showBreakdown && (
                      <>
                        <td className="py-3 px-4 text-gray-700">{emission.co2.toLocaleString()}</td>
                        <td className="py-3 px-4 text-gray-700">{emission.ch4.toFixed(2)}</td>
                        <td className="py-3 px-4 text-gray-700">{emission.n2o.toFixed(2)}</td>
                      </>
                    )}
                    <td className="py-3 px-4 font-semibold text-gray-900">
                      {(emission.totalCO2e / 1000).toFixed(2)} tCO₂e
                    </td>
                    {formData.showSourceRow && (
                      <td className="py-3 px-4">
                        <button className="text-blue-600 hover:text-blue-800 text-sm underline">
                          {emission.source}
                        </button>
                      </td>
                    )}
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-1">
                        {errors[emission.id] && (
                          <AlertTriangle className="w-4 h-4 text-red-500" title={errors[emission.id]} />
                        )}
                        {warnings[emission.id] && (
                          <Info className="w-4 h-4 text-yellow-500" title={warnings[emission.id]} />
                        )}
                        {emission.outlier && (
                          <div className="w-2 h-2 bg-yellow-400 rounded-full" title={t('workflow.calculation.scope1.table.outlierDetected')} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Validation Messages */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="text-red-800 font-medium mb-2">Errors</h4>
            <ul className="space-y-1">
              {Object.values(errors).map((error, index) => (
                <li key={index} className="text-red-700 text-sm flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {Object.keys(warnings).length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-yellow-800 font-medium mb-2">Warnings</h4>
            <ul className="space-y-1">
              {Object.values(warnings).map((warning, index) => (
                <li key={index} className="text-yellow-700 text-sm flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Methodology Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-2">{t('workflow.calculation.scope1.methodology.title')}</h4>
          <p className="text-sm text-gray-600">
            {t('workflow.calculation.scope1.methodology.description')}
          </p>
        </div>

        {/* Navigation handled by WorkflowTabView */}
      </div>
    </div>
  );
};

export default Scope1;
